/**
 * Build `srcset` strings for images stored in our own bucket.
 *
 * The backend writes three objects per upload — `<uuid>.webp` at up to 1200px
 * plus `<uuid>-400.webp` and `<uuid>-800.webp` — so the variant URLs are
 * derivable from the stored one by string manipulation, with no extra field on
 * the product and no lookup. See Backend/src/modules/upload/upload.service.ts;
 * IMAGE_VARIANT_WIDTHS there and VARIANT_WIDTHS here must agree.
 *
 * Why the narrow guard below: not every URL in the database is one of ours.
 * Images imported from a pasted link fall back to hot-linking the source when
 * the bucket write fails, and anything uploaded before the WebP change is still
 * a .png or .jpg with no variants beside it. Guessing wrong produces a 404 that
 * the browser renders as a broken image, so we only derive variants for keys we
 * are certain we wrote: a UUID filename with a .webp extension.
 */

const VARIANT_WIDTHS = [400, 800]
const BASE_WIDTH = 1200

/** Matches `<uuid>.webp`, the only shape uploadImage() produces. */
const OURS = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.webp$/i

/** @param {string} url @param {number} width */
const variant = (url, width) => url.replace(/\.webp$/, `-${width}.webp`)

/**
 * @param {string | undefined | null} url
 * @returns {string | undefined} a srcset, or undefined when the URL is not one
 *   we can derive variants for — in which case the caller's plain `src` stands.
 */
export function imageSrcSet(url) {
  if (typeof url !== 'string' || !OURS.test(url)) return undefined
  return [
    ...VARIANT_WIDTHS.map((w) => `${variant(url, w)} ${w}w`),
    `${url} ${BASE_WIDTH}w`,
  ].join(', ')
}

/**
 * Smallest stored rendition, for places that show a fixed small thumbnail.
 *
 * Falls back to the original URL when it is not ours, so callers can use this
 * unconditionally as the `src`.
 *
 * @param {string | undefined | null} url
 */
export function imageThumb(url) {
  if (typeof url !== 'string' || !OURS.test(url)) return url ?? ''
  return variant(url, VARIANT_WIDTHS[0])
}

/**
 * `sizes` values, named rather than inlined so the same grid does not drift
 * between ProductCard, the shop page and the wishlist.
 *
 * These describe the CSS width of the rendered image, which is what lets the
 * browser pick a candidate before stylesheets are applied. A value that is too
 * large wastes bytes; too small renders soft on high-DPI screens.
 */
export const SIZES = {
  /** Product grid: 2 up on phones, 3 on tablets, 4 on desktop. */
  card: '(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw',
  /** Product grid in list mode: a fixed square thumbnail. */
  cardList: '128px',
  /** Main product image: full width on phones, half the page on desktop. */
  gallery: '(min-width: 1024px) 50vw, 100vw',
  /** Gallery filmstrip and other fixed thumbnails. */
  thumb: '80px',
  /** Full-bleed hero. */
  hero: '100vw',
}
